import { useMemo, useState, useContext } from "react";
import { Link } from "react-router-dom";
import Chart from "react-apexcharts";
import axios from "axios";
import { toast } from "react-toastify";
import {
  CiShop,
  CiHeart,
  CiStar,
  CiWallet,
} from "react-icons/ci";
import AccountLayout from "./AccountLayout";
import UserDataFetcher from "../../../libs/getUserbyUserId";
import { useAddresses } from "../libs/useAddresses";
import useMyOrders from "../libs/useMyOrders";
import { useWishlist } from "../libs/useWishlist";
import { usePendingReviews, useOrderNotifications } from "../libs/useReviews";
import { AuthContext } from "../../../Context/AuthContext";
import { AccountProfileSkeleton } from "./AccountSkeletons";
import "./UserProfile.css";

const STATUS_COLORS = {
  Pending: "#f59e0b",
  Processing: "#284697",
  Shipped: "#8b5cf6",
  Delivered: "#10b981",
  Canceled: "#ef4444",
};

const STATUS_ORDER = ["Pending", "Processing", "Shipped", "Delivered", "Canceled"];

const getGreeting = () => {
  const hour = new Date().getHours();
  if (hour < 12) return "Good morning";
  if (hour < 17) return "Good afternoon";
  return "Good evening";
};

const getInitials = (first = "", last = "") =>
  `${first.charAt(0)}${last.charAt(0)}`.toUpperCase() || "U";

const buildMonthlySpend = (orders) => {
  const months = [];
  const now = new Date();
  for (let i = 5; i >= 0; i -= 1) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    months.push({
      key: `${d.getFullYear()}-${d.getMonth()}`,
      label: d.toLocaleDateString(undefined, { month: "short" }),
    });
  }
  const totals = Object.fromEntries(months.map((m) => [m.key, 0]));
  orders.forEach((order) => {
    if (order.status === "Canceled" || order.paymentStatus !== "paid") return;
    const created = new Date(order.createdAt);
    const key = `${created.getFullYear()}-${created.getMonth()}`;
    if (key in totals) totals[key] += order.totalAmount || 0;
  });
  return {
    categories: months.map((m) => m.label),
    data: months.map((m) => totals[m.key]),
  };
};

const UserProfile = () => {
  const { isLoading, isError, userData } = UserDataFetcher();
  const { data: addresses = [] } = useAddresses();
  const { data: orders = [], isLoading: ordersLoading } = useMyOrders();
  const { data: wishlist = [] } = useWishlist();
  const { data: pendingReviews = [] } = usePendingReviews();
  const { data: orderNotifications = [] } = useOrderNotifications();
  const { token, user } = useContext(AuthContext);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({});

  const profile = userData?.user;
  const firstName = profile?.first_name || "there";
  const defaultAddress = addresses.find((a) => a.isDefault) || addresses[0];

  const stats = useMemo(() => {
    const paidOrders = orders.filter((o) => o.paymentStatus === "paid");
    const totalSpent = paidOrders.reduce((sum, o) => sum + (o.totalAmount || 0), 0);
    const activeOrders = orders.filter(
      (o) => !["Delivered", "Canceled"].includes(o.status)
    ).length;
    return {
      totalOrders: orders.length,
      totalSpent,
      activeOrders,
      savedItems: wishlist.length,
      pendingReviews: pendingReviews.length,
    };
  }, [orders, wishlist, pendingReviews]);

  const statusBreakdown = useMemo(() => {
    const counts = STATUS_ORDER.reduce((acc, s) => ({ ...acc, [s]: 0 }), {});
    orders.forEach((o) => {
      const status = STATUS_ORDER.includes(o.status) ? o.status : "Pending";
      counts[status] += 1;
    });
    return STATUS_ORDER.map((status) => ({
      status,
      count: counts[status],
    })).filter((item) => item.count > 0);
  }, [orders]);

  const monthlySpend = useMemo(() => buildMonthlySpend(orders), [orders]);

  const pieOptions = useMemo(
    () => ({
      chart: { type: "donut", toolbar: { show: false } },
      labels: statusBreakdown.map((s) => s.status),
      colors: statusBreakdown.map((s) => STATUS_COLORS[s.status]),
      legend: { position: "bottom", fontFamily: "Montserrat, sans-serif" },
      dataLabels: { enabled: true },
      plotOptions: {
        pie: {
          donut: {
            size: "62%",
            labels: {
              show: true,
              total: {
                show: true,
                label: "Orders",
                fontFamily: "Montserrat, sans-serif",
                formatter: () => String(orders.length),
              },
            },
          },
        },
      },
    }),
    [statusBreakdown, orders.length]
  );

  const pieSeries = statusBreakdown.map((s) => s.count);

  const barOptions = useMemo(
    () => ({
      chart: { toolbar: { show: false }, fontFamily: "Montserrat, sans-serif" },
      xaxis: { categories: monthlySpend.categories },
      colors: ["#284697"],
      plotOptions: { bar: { borderRadius: 6, columnWidth: "48%" } },
      dataLabels: { enabled: false },
      yaxis: {
        labels: {
          formatter: (val) =>
            val >= 1000 ? `₦${(val / 1000).toFixed(0)}k` : `₦${Math.round(val)}`,
        },
      },
      tooltip: {
        y: {
          formatter: (val) =>
            val.toLocaleString("en-US", { style: "currency", currency: "NGN" }),
        },
      },
    }),
    [monthlySpend.categories]
  );

  const barSeries = [{ name: "Spending", data: monthlySpend.data }];

  const recentOrders = useMemo(
    () =>
      [...orders]
        .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
        .slice(0, 4),
    [orders]
  );

  const startEdit = () => {
    setForm({
      first_name: profile?.first_name || "",
      last_name: profile?.last_name || "",
      phone_number: profile?.phone_number || "",
    });
    setEditing(true);
  };

  const handleSave = async () => {
    try {
      await axios.patch(
        `${process.env.REACT_APP_SERVER_URL}/users/update/${user.userId}`,
        form,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Profile updated");
      setEditing(false);
      window.location.reload();
    } catch (err) {
      toast.error(err.response?.data?.message || "Update failed");
    }
  };

  if (isLoading) {
    return (
      <AccountLayout loading>
        <AccountProfileSkeleton />
      </AccountLayout>
    );
  }

  if (isError) {
    return (
      <AccountLayout title="Profile Overview">
        <p className="account-error">Error loading profile.</p>
      </AccountLayout>
    );
  }

  return (
    <AccountLayout title="Profile Overview">
      <div className="profile-overview">
        <header className="profile-overview__hero">
          <div className="profile-overview__hero-text">
            <p className="profile-overview__greeting">
              {getGreeting()}, {firstName}!
            </p>
            <h2>Welcome back to your STEX account</h2>
            <p>
              Track orders, manage saved items, and see your shopping activity at a glance.
            </p>
          </div>
          <div className="profile-overview__avatar" aria-hidden="true">
            {getInitials(profile?.first_name, profile?.last_name)}
          </div>
        </header>

        <div className="profile-overview__stats">
          <div className="profile-stat-card">
            <span className="profile-stat-card__icon profile-stat-card__icon--blue">
              <CiShop />
            </span>
            <div>
              <strong>{stats.totalOrders}</strong>
              <span>Total orders</span>
            </div>
          </div>
          <div className="profile-stat-card">
            <span className="profile-stat-card__icon profile-stat-card__icon--red">
              <CiWallet />
            </span>
            <div>
              <strong>
                {stats.totalSpent.toLocaleString("en-US", {
                  style: "currency",
                  currency: "NGN",
                  maximumFractionDigits: 0,
                })}
              </strong>
              <span>Total spent</span>
            </div>
          </div>
          <div className="profile-stat-card">
            <span className="profile-stat-card__icon profile-stat-card__icon--yellow">
              <CiHeart />
            </span>
            <div>
              <strong>{stats.savedItems}</strong>
              <span>Saved items</span>
            </div>
          </div>
          <div className="profile-stat-card">
            <span className="profile-stat-card__icon profile-stat-card__icon--purple">
              <CiStar />
            </span>
            <div>
              <strong>{stats.pendingReviews}</strong>
              <span>Pending reviews</span>
            </div>
          </div>
        </div>

        <div className="profile-overview__charts">
          <section className="profile-chart-card">
            <h3>Order status</h3>
            <p className="profile-chart-card__sub">
              {stats.activeOrders} active order{stats.activeOrders !== 1 ? "s" : ""} in progress
            </p>
            {ordersLoading ? (
              <p className="profile-chart-card__empty">Loading chart...</p>
            ) : pieSeries.length > 0 ? (
              <Chart options={pieOptions} series={pieSeries} type="donut" height={280} />
            ) : (
              <div className="profile-chart-card__empty">
                <p>No orders yet.</p>
                <Link to="/market" className="account-btn account-btn--secondary">
                  Start shopping
                </Link>
              </div>
            )}
          </section>

          <section className="profile-chart-card">
            <h3>Spending activity</h3>
            <p className="profile-chart-card__sub">Paid orders over the last 6 months</p>
            {ordersLoading ? (
              <p className="profile-chart-card__empty">Loading chart...</p>
            ) : monthlySpend.data.some((v) => v > 0) ? (
              <Chart options={barOptions} series={barSeries} type="bar" height={280} />
            ) : (
              <div className="profile-chart-card__empty">
                <p>No spending data yet. Your purchase history will appear here.</p>
              </div>
            )}
          </section>
        </div>

        <div className="profile-overview__grid">
          <div className="account-card profile-details-card">
            <div className="account-card__header">
              <h2>Profile details</h2>
            </div>
            <div className="account-card__body">
              {!editing ? (
                <>
                  <h3>
                    {profile?.first_name} {profile?.last_name}
                  </h3>
                  <p>{profile?.email}</p>
                  <p>{profile?.phone_number || "No phone number added"}</p>
                </>
              ) : (
                <div className="account-profile-form">
                  <input
                    value={form.first_name}
                    onChange={(e) => setForm({ ...form, first_name: e.target.value })}
                    placeholder="First name"
                  />
                  <input
                    value={form.last_name}
                    onChange={(e) => setForm({ ...form, last_name: e.target.value })}
                    placeholder="Last name"
                  />
                  <input
                    value={form.phone_number}
                    onChange={(e) => setForm({ ...form, phone_number: e.target.value })}
                    placeholder="Phone number"
                  />
                  <div className="account-profile-form__actions">
                    <button type="button" className="account-btn account-btn--primary" onClick={handleSave}>
                      Save changes
                    </button>
                    <button type="button" className="account-btn account-btn--ghost" onClick={() => setEditing(false)}>
                      Cancel
                    </button>
                  </div>
                </div>
              )}
            </div>
            {!editing && (
              <div className="account-card__footer">
                <button type="button" className="account-btn account-btn--primary" onClick={startEdit}>
                  Edit profile
                </button>
              </div>
            )}
          </div>

          <div className="account-card">
            <div className="account-card__header">
              <h2>Default address</h2>
            </div>
            <div className="account-card__body">
              {defaultAddress ? (
                <>
                  <h3>{defaultAddress.full_name}</h3>
                  <p>
                    {defaultAddress.address}, {defaultAddress.city}, {defaultAddress.state}
                  </p>
                  <p>{defaultAddress.phone_number}</p>
                </>
              ) : (
                <p>No default shipping address yet. Add one for faster checkout.</p>
              )}
            </div>
            <div className="account-card__footer">
              <Link to="/account/addresses" className="account-btn account-btn--secondary">
                Manage addresses
              </Link>
            </div>
          </div>

          <div className="account-card profile-recent-card">
            <div className="account-card__header">
              <h2>Order updates</h2>
            </div>
            <div className="account-card__body profile-recent-list">
              {orderNotifications.length === 0 ? (
                <p>No recent order updates.</p>
              ) : (
                <ul>
                  {orderNotifications.slice(0, 5).map((note) => (
                    <li key={note.id}>
                      <Link to={`/account/orders/${note.id}`}>{note.orderNumber || "Order"}</Link>
                      <small>{note.message}</small>
                      <small>{new Date(note.date).toLocaleDateString()}</small>
                    </li>
                  ))}
                </ul>
              )}
            </div>
            <div className="account-card__footer">
              <Link to="/account/inbox" className="account-btn account-btn--secondary">
                View inbox
              </Link>
            </div>
          </div>

          <div className="account-card profile-recent-card">
            <div className="account-card__header">
              <h2>Recent orders</h2>
            </div>
            <div className="account-card__body profile-recent-list">
              {recentOrders.length === 0 ? (
                <p>No recent orders.</p>
              ) : (
                <ul>
                  {recentOrders.map((order) => (
                    <li key={order._id}>
                      <Link to={`/account/orders/${order._id}`}>{order.orderNumber}</Link>
                      <span className={`profile-recent-status profile-recent-status--${order.status.toLowerCase()}`}>
                        {order.status}
                      </span>
                      <small>{new Date(order.createdAt).toLocaleDateString()}</small>
                    </li>
                  ))}
                </ul>
              )}
            </div>
            <div className="account-card__footer">
              <Link to="/account/orders" className="account-btn account-btn--ghost">
                View all orders
              </Link>
            </div>
          </div>
        </div>

        <nav className="profile-overview__quick" aria-label="Quick links">
          <Link to="/account/orders">My orders</Link>
          <Link to="/account/saved">Saved items</Link>
          <Link to="/market/track-order">Track order</Link>
          <Link to="/account/reviews">Write a review</Link>
          <Link to="/account/settings">Settings</Link>
        </nav>
      </div>
    </AccountLayout>
  );
};

export default UserProfile;
